import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { DisclaimerCheckbox } from '../components/Disclaimer';
import { validatePassword } from '../lib/passwordRules';

function PasswordRequirements({ password, show }) {
  if (!show) return null;
  const checks = validatePassword(password);
  return (
    <div style={{ marginTop: 8, marginBottom: 4, padding: '10px 12px', background: 'rgba(184,137,58,0.10)', borderRadius: 8, border: '1px solid rgba(184,137,58,0.22)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#B8893A', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Password must include</div>
      {checks.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.ok ? '#1A7F37' : '#6B6358', lineHeight: 1.6 }}>
          <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', fontWeight: 700 }}>{c.ok ? '✓' : '○'}</span>
          <span style={{ textDecoration: c.ok ? 'none' : 'none' }}>{c.label}</span>
        </div>
      ))}
    </div>
  );
}

export function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: window.location.origin + '/reset-password',
    });
    setForgotLoading(false);
    if (error) { setForgotMsg('Error: ' + error.message); return; }
    setForgotMsg('Check your email for a password reset link.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }

    // Block sign-in if the account has been suspended by an admin.
    // The self-select RLS on user_suspensions lets us read our own row.
    const { data: suspension } = await supabase
      .from('user_suspensions').select('reason').eq('user_id', data.user.id).maybeSingle();
    if (suspension) {
      await supabase.auth.signOut();
      setError('This account is suspended. ' + (suspension.reason ? `Reason: ${suspension.reason}. ` : '') + 'Contact support@renterrep.com.');
      setLoading(false);
      return;
    }

    const user = { name: data.user.user_metadata?.name || email.split('@')[0], email: data.user.email, role: data.user.user_metadata?.role || 'tenant', id: data.user.id };
    onLogin(user);
    setLoading(false);
    console.log('Navigating to:', redirect);
    setTimeout(() => navigate(redirect), 100);
  };

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: '#0F2C4E', marginBottom: 4, fontWeight: 400 }}>Renter<span style={{ color: '#C9972A' }}>Rep</span></div>
          <div style={{ fontSize: 14, color: '#6B6358' }}>Log in to your account</div>
        </div>
        <div style={{
          background: '#FAF6EC',
          borderRadius: 16,
          padding: '24px 22px',
          border: '1px solid rgba(184,137,58,0.22)',
          boxShadow: '0 4px 12px rgba(120,90,30,0.10)',
        }}>
        {!showForgot ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required autoFocus />
            </div>
            <div className="form-group">
              <label>Password</label>
<div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B6358', display: 'flex', alignItems: 'center' }} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            </div>
            {error && <div style={{ color: '#C0392B', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: loading ? 0.7 : 1 }} disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotSubmit}>
            <div style={{ fontSize: 14, color: '#0F2C4E', marginBottom: 12, textAlign: 'center', fontWeight: 700 }}>Reset your password</div>
            <div style={{ fontSize: 13, color: '#6B6358', marginBottom: 16, textAlign: 'center' }}>Enter your email and we'll send you a link to reset your password.</div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" required autoFocus />
            </div>
            {forgotMsg && <div style={{ color: forgotMsg.startsWith('Error') ? '#C0392B' : '#1A7F37', fontSize: 13, marginBottom: 12 }}>{forgotMsg}</div>}
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: forgotLoading ? 0.7 : 1, marginBottom: 10 }} disabled={forgotLoading}>
              {forgotLoading ? 'Sending...' : 'Send reset link'}
            </button>
            <button type="button" onClick={() => { setShowForgot(false); setForgotMsg(''); }} style={{ background: 'none', border: 'none', color: '#0F2C4E', cursor: 'pointer', fontSize: 13, width: '100%' }}>← Back to login</button>
          </form>
        )}
        </div>
        {!showForgot && (
          <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6B6358' }}>
            Don't have an account?{' '}
            <button onClick={() => navigate('/signup?redirect=' + encodeURIComponent(redirect))} style={{ background: 'none', border: 'none', color: '#0F2C4E', cursor: 'pointer', fontWeight: 700 }}>Sign Up</button>
            <span style={{ margin: '0 8px', color: 'rgba(184,137,58,0.45)' }}>·</span>
            <button onClick={() => { setShowForgot(true); setForgotEmail(email); }} style={{ background: 'none', border: 'none', color: '#0F2C4E', cursor: 'pointer', fontWeight: 700 }}>Forgot password?</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Signup({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [role, setRole] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const passwordChecks = validatePassword(password);
  const passwordAllOk = passwordChecks.every(c => c.ok);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const showMismatchError = confirmPassword.length > 0 && !passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) { setError('Please select whether you are a renter or a property management/property manager.'); return; }
    if (!passwordAllOk) { setError('Your password does not meet all the requirements below.'); return; }
    if (!passwordsMatch) { setError('The two passwords do not match. Please re-enter them.'); return; }
    if (!agreed) { setError('Please agree to the terms to continue.'); return; }
    setLoading(true);
    setError('');

    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { name, role } }
    });

    if (error) { setError(error.message); setLoading(false); return; }

    console.log('Signup response:', { user: data.user, session: data.session });
    console.log('Signup redirect target:', redirect);

    if (!data.session) {
      setError('Please check your email to confirm your account, then log in.');
      setLoading(false);
      return;
    }

    // Populate profiles table so the admin user list can see this user.
    // RLS lets the user insert their own row.
    if (data.user?.id) {
      await supabase.from('profiles').insert({
        id: data.user.id, email, name, role,
      });
    }

    const user = { name, email, role, id: data.user?.id };
    onLogin(user);
    setLoading(false);
    console.log('Navigating to:', redirect);
    setTimeout(() => navigate(redirect), 100);
  };

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3', padding: 16 }}>
      <div style={{ maxWidth: 460, margin: '0 auto', paddingTop: 24 }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: '#0F2C4E', marginBottom: 4, fontWeight: 400 }}>Renter<span style={{ color: '#C9972A' }}>Rep</span></div>
          <div style={{ fontSize: 14, color: '#6B6358' }}>Create your free account</div>
        </div>

        <div style={{
          background: '#FAF6EC',
          borderRadius: 16,
          padding: '24px 22px',
          border: '1px solid rgba(184,137,58,0.22)',
          boxShadow: '0 4px 12px rgba(120,90,30,0.10)',
        }}>
        {/* Role selection — pill buttons */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F2C4E', marginBottom: 10, textAlign: 'center' }}>I am a...</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setRole('tenant')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '10px 20px',
                borderRadius: 999,
                flex: '0 0 260px',
                background: role === 'tenant' ? '#0F2C4E' : '#F5EFE3',
                border: role === 'tenant' ? '2px solid #0F2C4E' : '2px solid rgba(184,137,58,0.32)',
                color: role === 'tenant' ? '#fff' : '#0F2C4E',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                transition: 'all 0.15s',
                boxShadow: role === 'tenant' ? '0 2px 8px rgba(15,44,78,0.20)' : 'none',
              }}
            >
              <span style={{ fontSize: 16 }}>🏠</span>
              Renter / Tenant
            </button>
            <button
              type="button"
              onClick={() => setRole('manager')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '10px 20px',
                borderRadius: 999,
                flex: '0 0 260px',
                background: role === 'manager' ? '#0F2C4E' : '#F5EFE3',
                border: role === 'manager' ? '2px solid #0F2C4E' : '2px solid rgba(184,137,58,0.32)',
                color: role === 'manager' ? '#fff' : '#0F2C4E',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                transition: 'all 0.15s',
                boxShadow: role === 'manager' ? '0 2px 8px rgba(15,44,78,0.20)' : 'none',
              }}
            >
              <span style={{ fontSize: 16 }}>🏢</span>
              Property Management
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required />
          </div>
          <div className="form-group">
            <label>Password</label>
<div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                placeholder="Create a strong password"
                required
                minLength={8}
                style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B6358', display: 'flex', alignItems: 'center' }} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            <PasswordRequirements password={password} show={passwordFocused || password.length > 0} />
          </div>

          <div className="form-group">
            <label>Confirm password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter the same password"
                required
                style={{
                  paddingRight: 40,
                  width: '100%',
                  boxSizing: 'border-box',
                  borderColor: showMismatchError ? '#C0392B' : (passwordsMatch ? '#1A7F37' : undefined),
                }}
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B6358', display: 'flex', alignItems: 'center' }} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {showMismatchError && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#C0392B', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠️</span> The passwords don't match yet.
              </div>
            )}
            {passwordsMatch && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#1A7F37', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>✓</span> Passwords match.
              </div>
            )}
          </div>

          <DisclaimerCheckbox checked={agreed} onChange={(checked) => setAgreed(checked)} />

          {error && <div style={{ color: '#C0392B', fontSize: 13, marginBottom: 12 }}>{error}</div>}

          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Free Account'}
          </button>
        </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6B6358' }}>
          Already have an account?{' '}
          <button onClick={() => navigate('/login?redirect=' + encodeURIComponent(redirect))} style={{ background: 'none', border: 'none', color: '#0F2C4E', cursor: 'pointer', fontWeight: 700 }}>Log In</button>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const pwChecks = validatePassword(password);
  const pwAllOk = pwChecks.every(c => c.ok);
  const pwMatch = password.length > 0 && password === confirmPassword;
  const pwMismatchVisible = confirmPassword.length > 0 && !pwMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!pwAllOk) { setError('Your new password does not meet all the requirements below.'); return; }
    if (!pwMatch) { setError('The two passwords do not match. Please re-enter them.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess(true);
    setTimeout(() => navigate('/login'), 2500);
  };

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: '#0F2C4E', marginBottom: 4, fontWeight: 400 }}>Renter<span style={{ color: '#C9972A' }}>Rep</span></div>
          <div style={{ fontSize: 14, color: '#6B6358' }}>Set a new password</div>
        </div>

        <div style={{
          background: '#FAF6EC',
          borderRadius: 16,
          padding: '24px 22px',
          border: '1px solid rgba(184,137,58,0.22)',
          boxShadow: '0 4px 12px rgba(120,90,30,0.10)',
        }}>
        {success ? (
          <div style={{ background: '#E8F5E9', border: '1px solid #1A7F37', borderRadius: 8, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
            <div style={{ color: '#1A7F37', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Password updated!</div>
            <div style={{ color: '#6B6358', fontSize: 13 }}>Redirecting to login...</div>
          </div>
        ) : !ready ? (
          <div style={{ textAlign: 'center', color: '#6B6358', fontSize: 14 }}>
            <div>Verifying your reset link...</div>
            <div style={{ fontSize: 12, marginTop: 8 }}>If this takes more than a few seconds, your link may have expired.</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>New password</label>
<div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" required minLength={8} autoFocus style={{ paddingRight: 40, width: '100%', boxSizing: 'border-box' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6C757D', display: 'flex', alignItems: 'center' }} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            <PasswordRequirements password={password} show={password.length > 0} />
            </div>
            <div className="form-group">
              <label>Confirm new password</label>
<div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter the same password"
                required
                style={{
                  paddingRight: 40,
                  width: '100%',
                  boxSizing: 'border-box',
                  borderColor: pwMismatchVisible ? '#C0392B' : (pwMatch ? '#1A7F37' : undefined),
                }}
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6C757D', display: 'flex', alignItems: 'center' }} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {pwMismatchVisible && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#C0392B', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠️</span> The passwords don't match yet.
              </div>
            )}
            {pwMatch && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#1A7F37', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>✓</span> Passwords match.
              </div>
            )}
            </div>
            {error && <div style={{ color: '#C0392B', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: loading ? 0.7 : 1 }} disabled={loading}>
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}
